<template>
  <main class="container">
    <section class="hero">
      <div>
        <h1>Refuges associés</h1>
        <p>
          Retrouvez l’ensemble de nos refuges partenaires. Consultez leurs animaux disponibles
          et découvrez comment adopter près de chez vous.
        </p>
      </div>

      <div class="tools">
        <div class="search" role="search">
          <span class="icon" aria-hidden="true"></span>
          <input
            type="text"
            placeholder="Rechercher un refuge ou une ville…"
            v-model="search"
          />
        </div>

        <button
          class="chip"
          type="button"
          :class="{ active: activeFilter === 'chien' }"
          @click="toggleFilter('chien')"
        >
          Chiens
        </button>

        <button
          class="chip"
          type="button"
          :class="{ active: activeFilter === 'chat' }"
          @click="toggleFilter('chat')"
        >
          Chats
        </button>

        <button
          class="chip"
          type="button"
          :class="{ active: activeFilter === 'nac' }"
          @click="toggleFilter('nac')"
        >
          NAC
        </button>
      </div>
    </section>

    <section class="grid">
      <article class="card" v-for="refuge in filteredRefuges" :key="refuge.slug">
      <div class="cover" :style="{ background: refuge.color }">
        <span class="badge">Partenaire</span>

        <div v-if="refuge.logo" class="logoCircle">
          <img :src="refuge.logo" :alt="'Logo ' + refuge.name" />
        </div>
      </div>

        <div class="content">
          <div class="titleRow">
            <div>
              <div class="name">{{ refuge.name }}</div>
              <div class="city">
                {{ refuge.city }}
                <span v-if="refuge.region"> • {{ refuge.region }}</span>
              </div>
            </div>
          </div>

          <div class="meta">
            <span class="pill">
              {{ refuge.animalsCount ? refuge.animalsCount + ' animaux' : 'Refuge partenaire' }}
            </span>
            <span class="pill">
              {{ refuge.categoriesLabel }}
            </span>
          </div>

          <div class="actions">
            <a
              class="link primary"
              :href="refuge.url"
              target="_blank"
              rel="noopener noreferrer"
            >
              Site du refuge
            </a>

            <a class="link" href="#">
              Fiche refuge
            </a>
          </div>
        </div>
      </article>
    </section>

    <p class="foot">
      Vous êtes un refuge et souhaitez apparaître ici ? Contactez-nous pour devenir partenaire.
    </p>
  </main>
</template>

<script setup>
import { computed, ref } from "vue"

const search = ref("")
const activeFilter = ref(null)

const refuges = ref([
  {
    slug: "sos-animaux-le-broc",
    name: "SOS Animaux",
    city: "Le Broc (63500)",
    region: "Auvergne-Rhône-Alpes",
    categories: ["chien", "chat"],
    url: "https://sosanimauxlebroc.fr/",
    animalsCount: null,
    color: "#fff2d6",
    logo:"https://admin.asso-refuge.fr/images/1765911875_logo.png",
  },
  {
    slug: "refuge-berauds-romans",
    name: "Refuge Bérauds",
    city: "Romans-sur-Isère",
    region: "Auvergne-Rhône-Alpes",
    categories: ["chat", "chien", "nac"],
    url: "https://refugeromans26.fr",
    animalsCount: null,
    color: "#c9d5df",
    logo:"https://admin.asso-refuge.fr/images/1767544351_logo.JPG",

  },
  {
    slug: "spa-montelimar",
    name: "SPA Montélimar",
    logo: "https://admin.asso-refuge.fr/images/1743350425_logo.png",
    city: "Montélimar",
    region: "Auvergne-Rhône-Alpes",
    categories: ["chat", "chien", "nac"],
    url: "https://spa-montelimar.fr/",
    animalsCount: null,
    color: "#dfe6db",
  },
  {
    slug: "spa-savoie",
    name: "SPA de Savoie",
    city: "Chamonix",
    region: "Auvergne-Rhône-Alpes",
    categories: ["chat", "chien", "nac"],
    url: "https://spadesavoie.fr/",
    animalsCount: null,
    logo:"https://spadesavoie.fr/img/newlogo.8e6fe85e.png",
    color: "#e4d7f2",
  },
  {
    slug: "spa-dunkerque",
    name: "SPA Dunkerque",
    city: "Dunkerque",
    region: "Hauts-de-France",
    categories: ["chat", "chien", "nac"],
    url: "https://spa-dunkerque.fr/",
    animalsCount: null,
    color: "#d8efe9",
    logo:"https://spa-dunkerque.fr/img/newlogo.d6253520.png",
  },
  {
    slug: "un-reve-un-cheval-une-famille",
    name: "Un rêve, Un cheval, Une famille",
    city: "Frambouhans (Doubs)",
    region: "Bourgogne-Franche-Comté",
    categories: ["chat", "chien", "nac", "ferme"],
    url: "https://unreveunchevalunefamille.com/",
    logo:"https://admin.asso-refuge.fr/images/1757457335_logo.jpg",
    animalsCount: null,
    color: "#f2e3d7",
  },
  {
    slug: "sadf-cherisy",
    name: "S.A.D.F",
    city: "Chérisy (28500)",
    region: "Centre-Val de Loire",
    categories: ["chat"],
    url: "https://www.sadf.fr/",
    animalsCount: null,
    color: "#f1f1f1",
    logo:"https://admin.asso-refuge.fr/images/1757504910_logo.jpg",
  },
  {
    slug: "refuge-cabries",
    name: "Refuge de Cabriès",
    city: "Cabriès",
    region: "Provence-Alpes-Côte d’Azur",
    categories: ["chien", "chat"],
    url: "https://cda-refugecabries.fr/",
    animalsCount: null,
    color: "#d6e6ff",
    logo:"https://admin.asso-refuge.fr/images/1758830626_logo.jpg",
  },
  {
    slug: "opla-deux-sevres",
    name: "OPLA",
    city: "Deux-Sèvres",
    region: "Nouvelle-Aquitaine",
    categories: ["chien", "chat"],
    url: "https://onpeutlesaider.fr/",
    animalsCount: null,
    color: "#fde7f0",
    logo:"https://admin.asso-refuge.fr/images/1758830902_logo.jpg",
  },

  {
    slug: "relais-des-animaux",
    name: "Le Relais Des Animaux",
    city: "Buxières-lès-Villiers",
    region: "Grand Est",
    categories: ["chien", "chat", "ferme"],
    url: "https://lerelaisdesanimaux.fr/",
    animalsCount: null,
    color: "#e6f7d6",
    logo:"https://admin.asso-refuge.fr/images/1762468145_logo.png",
  },
  {
    slug: "ajpla-nice",
    name: "AJPLA",
    city: "Nice",
    region: "Provence-Alpes-Côte d’Azur",
    categories: ["chien", "chat"],
    url: "",
    animalsCount: null,
    color: "#e9e9ff",
    logo:"https://admin.asso-refuge.fr/images/1768033888_logo.jpg"
  },

{
    slug: "entraide-villejuif",
    name: "Entraide Villejuif",
    city: "Villejuif",
    region: "Île-de-France",
    categories: ["chat", "chien"],
    url: "https://entraidechatsvillejuif.fr/",
    animalsCount: null,
    color: "#d6b38c",
    logo:"https://admin.asso-refuge.fr/images/1751008734_logo.jpg",
  },

  {
    slug: "spa-dda-beziers",
    name: "SPA DDA de Béziers",
    city: "Béziers",
    region: "Occitanie",
    categories: ["chien", "chat"],
    url: "https://spa-dda-beziers.fr/",
    animalsCount: null,
    color: "#ffe8cc",
    logo: "https://admin.asso-refuge.fr/images/1759783944_logo.png",
  },
  {
    slug: "spa-tarbes",
    name: "SPA Tarbes",
    city: "Tarbes",
    region: "Occitanie",
    categories: ["chien", "chat"],
    url: "https://spatarbes.com/",
    animalsCount: null,
    color: "#cce5ff",
    logo: "https://admin.asso-refuge.fr/images/1772901773_logo.png",
  },
  {
    slug: "la-porte-de-lespoir",
    name: "La Porte de l'Espoir",
    city: "Alsace / Vosges",
    region: "Grand Est",
    categories: ["chien", "chat", "nac", "ferme"],
    url: "https://laportedelespoir.org/",
    animalsCount: null,
    color: "#d4f0d4",
    logo: "https://admin.asso-refuge.fr/images/1772661078_logo.jpg",
  },
  {
    slug: "spa-dole",
    name: "SPA de Dole",
    city: "Dole",
    region: "Bourgogne-Franche-Comté",
    categories: ["chien", "chat"],
    url: "https://spadole.fr/",
    animalsCount: null,
    color: "#fff9cc",
    logo: "https://admin.asso-refuge.fr/images/1771177925_logo.png",
  },
  {
    slug: "vakanima",
    name: "Vakanima",
    city: "",
    region: "",
    categories: ["chien", "chat"],
    url: "https://vakanima.fr/",
    animalsCount: null,
    color: "#fce4ec",
    logo: "https://admin.asso-refuge.fr/documents/zefqz-69cc242d4f939.jpeg",
  },
  {
    slug: "association-simba",
    name: "Association Simba",
    city: "Sarthe",
    region: "Pays de la Loire",
    categories: ["chien", "chat"],
    url: "https://association-simba.fr/",
    animalsCount: null,
    color: "#ede7f6",
    logo: "https://admin.asso-refuge.fr/images/1727819280_logo.png",
  },
])

const refugesWithLabels = computed(() => {
  return refuges.value.map((r) => ({
    ...r,
    categoriesLabel: r.categories
      .filter((c) => ["chien", "chat", "nac", "ferme"].includes(c))
      .map((c) => {
        if (c === "chien") return "Chiens"
        if (c === "chat") return "Chats"
        if (c === "nac") return "NAC"
        if (c === "ferme") return "Animaux de la ferme"
        return c
      })
      .join(" • "),
  }))
})

const filteredRefuges = computed(() => {
  const q = search.value.trim().toLowerCase()

  return refugesWithLabels.value.filter((r) => {
    const matchesSearch =
      !q ||
      r.name.toLowerCase().includes(q) ||
      r.city.toLowerCase().includes(q) ||
      (r.region || "").toLowerCase().includes(q)

    const matchesFilter = !activeFilter.value || r.categories.includes(activeFilter.value)

    return matchesSearch && matchesFilter
  })
})

function toggleFilter(filter) {
  activeFilter.value = activeFilter.value === filter ? null : filter
}
</script>

<style src="../assets/css/refugeassocie.css" scoped></style>
